import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { GoogleMap } from '@angular/google-maps';
import { GooglePlaceDirective } from 'ngx-google-places-autocomplete';
import { Subject, filter, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/firebase/auth.service';
import { MapService } from 'src/app/services/map.service';
import { User } from '../../models/user.model';
import { FormService } from '../form/form.service';

@Component({
  selector: 'app-google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss'],
})
export class GoogleMapComponent implements OnInit, OnDestroy {
  @Input() form;
  @Input() required: boolean;
  @Output() formGroupChange = new EventEmitter<FormGroup>();

  @ViewChild('placesRef', { static: false }) placesRef: GooglePlaceDirective;
  @ViewChild('places') places: ElementRef;
  @ViewChild('markerElem') markerElem;
  @ViewChild('googleMap') googleMap: GoogleMap;

  isOpen = false;
  currentUser: User;
  center: google.maps.LatLngLiteral;
  zoom = 10;
  options = {
    disableDefaultUI: true,
  };
  markerOptions: google.maps.MarkerOptions = { draggable: false };
  markerPositions: google.maps.LatLngLiteral[] = [];
  chooseBtnDisabled = !this.markerPositions.length;
  address = '';

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private formService: FormService,
    private mapService: MapService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.getUser();

    this.form = this.fb.group({ googleMapLink: [null] });
    this.formGroupChange.emit(this.form);
    this.formService.location$
      .pipe(
        filter(
          value =>
            !!value &&
            value.googleMapLink !== this.form.get('googleMapLink').value
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.form.patchValue(value);
        this.address = value.googleMapLink;
        if (value.googleMapLink) {
          const location = this.mapService.createCoordinates(
            value.googleMapLink
          );
          if (!location) return;

          this.markerPositions = [location];
          this.zoom = 17;
          this.applyCenter(location, this.zoom);
        }
      });
  }

  ngOnDestroy(): void {
    this.formService.location$.next({ googleMapLink: null });
    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleAddressChange(address: any) {
    const location = this.mapService.normalizeLatLng({
      lat: address?.geometry?.location.lat(),
      lng: address?.geometry?.location.lng(),
    });
    if (!location) return;

    this.zoom = 17;

    this.markerPositions = [location];
    this.chooseBtnDisabled = false;
    const baseMapUrl = 'https://www.google.com/maps/search/?api=1&query=';
    this.address = `${baseMapUrl}${location.lat},${location.lng}`;
    this.applyCenter(location, this.zoom);
  }

  async createMapHandler() {
    await this.setInitialCenter();
    this.isOpen = true;
  }

  moveMap(e) {
    const { lat, lng } = e.latLng.toJSON();
    const baseMapUrl = 'https://www.google.com/maps/search/?api=1&query=';

    this.address = `${baseMapUrl}${lat},${lng}`;

    this.markerPositions = [
      {
        lat,
        lng,
      },
    ];
    this.chooseBtnDisabled = false;
    this.applyCenter({ lat, lng }, this.zoom);
  }

  onMapModalDidPresent() {
    if (!this.center) return;

    this.applyCenter(this.center, this.zoom);
  }

  cancelModal() {
    this.isOpen = false;
    if (!this.form.get('googleMapLink').value) {
      this.setInitialCenter();
      this.chooseBtnDisabled = true;
      this.markerPositions = [];
      this.address = '';
    } else {
      const location = this.mapService.createCoordinates(
        this.form.get('googleMapLink').value
      );
      if (!location) return;

      this.address = this.form.get('googleMapLink').value;
      this.markerPositions = [location];
      this.zoom = 17;
      this.applyCenter(location, this.zoom);
    }
  }

  resetLocation() {
    this.address = '';
    this.markerPositions = [];
    this.chooseBtnDisabled = true;
    this.formService.location$.next({
      googleMapLink: null,
    });
    this.form.get('googleMapLink').patchValue(null);
  }

  chooseLocation() {
    const { lat, lng } = this.markerPositions[0];
    const baseMapUrl = 'https://www.google.com/maps/search/?api=1&query=';

    this.formService.location$.next({
      googleMapLink: `${baseMapUrl}${lat},${lng}`,
    });
    this.cancelModal();
  }

  getUser() {
    if (!this.authService.isUserAuth()) return;
    const { uid } = this.authService.isUserAuth();
    this.authService
      .getUserByUid(uid)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (data: any) => {
        this.currentUser = data;
        this.setInitialCenter(!this.address && !this.markerPositions.length);
      });
  }

  private async setInitialCenter(useLatestUserLocation = false) {
    if (this.center && !useLatestUserLocation) return;

    const center = useLatestUserLocation
      ? await this.mapService.getApproximateUserCenter()
      : await this.mapService.getBestUserCenter(this.currentUser?.location);
    this.applyCenter(center, this.zoom);
  }

  private applyCenter(center: google.maps.LatLngLiteral, zoom = this.zoom) {
    this.center = center;
    this.zoom = zoom;

    setTimeout(() => {
      if (!this.googleMap?.googleMap) {
        return;
      }

      this.googleMap.googleMap.setCenter(center);
      this.googleMap.googleMap.setZoom(zoom);
    }, 100);
  }
}
